import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from '../../hooks';
import proofkitFilemakerService from '../../services/proofkitFilemakerService';
import { RefreshCw, Plus, Edit, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui';

const CAMPAIGNS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CAMPAIGNS || "campaigns";
const TEMPLATES_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_TEMPLATES || "templates";

export default function CampaignsList() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);

  const rows = useMemo(() => {
    const templatesMap = {};
    (Array.isArray(templates) ? templates : []).forEach((r) => {
      const fd = r?.fieldData || {};
      const id = fd?.ClavePrincipal || r?.recordId || "";
      const nombre = fd?.nombre || "";
      if (id) templatesMap[String(id)] = nombre || id;
    });
    return (Array.isArray(campaigns) ? campaigns : []).map((r) => {
      const fieldData = r?.fieldData || {};
      const id = r?.recordId || fieldData?.ClavePrincipal || "";
      const templateId = fieldData?.template_id || "";
      const templateName = templatesMap[String(templateId)] || "";
      return {
        id,
        nombre: fieldData?.nombre || "",
        estado: fieldData?.estado || "",
        template_name: templateName || "",
        fecha_envio: fieldData?.fecha_envio || "",
        total_recipients: Number(fieldData?.total_recipients || 0),
      };
    });
  }, [campaigns, templates]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const [campaignsRes, templatesRes] = await Promise.all([
        proofkitFilemakerService.getAllRecords(CAMPAIGNS_LAYOUT),
        proofkitFilemakerService.getAllRecords(TEMPLATES_LAYOUT),
      ]);

      setCampaigns(Array.isArray(campaignsRes?.data) ? campaignsRes.data : []);
      setTemplates(Array.isArray(templatesRes?.data) ? templatesRes.data : []);
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudieron cargar las campañas",
        variant: "destructive",
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const renderEstado = (estado) => {
    const v = String(estado || "").toLowerCase();
    if (!v) return <Badge variant="secondary">Sin estado</Badge>;
    if (v === "enviada" || v === "sent") return <Badge>Enviada</Badge>;
    if (v === "programada" || v === "scheduled") return <Badge variant="outline">Programada</Badge>;
    if (v === "borrador" || v === "draft") return <Badge variant="secondary">Borrador</Badge>;
    if (v === "cancelada" || v === "cancelled") return <Badge variant="destructive">Cancelada</Badge>;
    return <Badge variant="secondary">{estado}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Campañas</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchCampaigns} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button variant="outline" onClick={() => navigate("/campanas/single")}>
            <Send className="h-4 w-4 mr-2" />
            Email individual
          </Button>
          <Button onClick={() => navigate("/campanas/new")}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva Campaña
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Gestionar Campañas</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Cargando campañas...</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Plantilla</TableHead>
                    <TableHead>Fecha Envío</TableHead>
                    <TableHead>Destinatarios</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length ? (
                    rows.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{c.nombre || "Sin nombre"}</TableCell>
                        <TableCell>{renderEstado(c.estado)}</TableCell>
                        <TableCell>{c.template_name || "-"}</TableCell>
                        <TableCell>{c.fecha_envio || "-"}</TableCell>
                        <TableCell>{Number.isFinite(c.total_recipients) ? c.total_recipients : 0}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/campanas/${c.id}/edit`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center">
                        No hay campañas.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
